import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  Google as GoogleIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { systemAdminInviteService } from '../../services/systemAdminInviteService';
import { getSystemRoleDisplayName } from '../../utils/systemPermissions';
import logger from '../../utils/logger';

const STEPS = ['Verify Invitation', 'Create Account', 'Profile Picture', 'Complete'];

/**
 * SystemAdminOnboardingWizard
 * Simplified onboarding wizard for IT professionals invited as system admins.
 * No family name step, no family code required.
 * Creates a user with accountType: 'system_admin'.
 */
const SystemAdminOnboardingWizard = () => {
  const navigate = useNavigate();
  const { inviteCode } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [loadingInvitation, setLoadingInvitation] = useState(true);
  const [finalized, setFinalized] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    profilePicture: null,
    userId: null,
    ssoProvider: null,
    ssoUser: null
  });

  // Load and verify invitation on mount
  useEffect(() => {
    const loadInvitation = async () => {
      if (!inviteCode) {
        setError('No invitation code provided. You need a valid system admin invitation to access this page.');
        setLoadingInvitation(false);
        return;
      }

      try {
        setLoadingInvitation(true);
        const inviteData = await systemAdminInviteService.getInvitation(inviteCode);

        if (inviteData.status !== 'pending') {
          setError(`This invitation is ${inviteData.status}. Please contact the administrator for a new invitation.`);
          setLoadingInvitation(false);
          return;
        }

        setInvitation(inviteData);
        setFormData(prev => ({
          ...prev,
          email: inviteData.inviteeEmail || '',
          firstName: inviteData.inviteeName?.split(' ')[0] || '',
          lastName: inviteData.inviteeName?.split(' ').slice(1).join(' ') || ''
        }));

        // Auto-advance past verification step
        setActiveStep(1);
      } catch (err) {
        logger.error('Error loading system admin invitation:', err);
        setError(err.message || 'Failed to load invitation');
      } finally {
        setLoadingInvitation(false);
      }
    };

    loadInvitation();
  }, [inviteCode]);

  // Auto-finalize when reaching the Complete step
  useEffect(() => {
    if (activeStep === 3 && !loading && !finalized) {
      finalizeAccount();
    }
  }, [activeStep, finalized]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateStep = () => {
    switch (activeStep) {
      case 0: // Verify Invitation
        return !!invitation;

      case 1: // Create Account
        if (formData.ssoProvider) {
          return formData.firstName && formData.lastName;
        }
        return formData.email && formData.password && formData.firstName && formData.lastName;

      case 2: // Profile Picture (optional)
        return true;

      default:
        return true;
    }
  };

  const handleGoogleSSO = async () => {
    try {
      setLoading(true);
      setError(null);

      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const displayName = user.displayName || '';
      const nameParts = displayName.split(' ');

      setFormData(prev => ({
        ...prev,
        ssoProvider: 'google',
        ssoUser: user,
        email: user.email || '',
        firstName: prev.firstName || nameParts[0] || '',
        lastName: prev.lastName || nameParts.slice(1).join(' ') || '',
        userId: user.uid
      }));

      setActiveStep(prev => prev + 1);
    } catch (err) {
      logger.error('Google SSO error:', err);
      setError(err.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftSSO = async () => {
    try {
      setLoading(true);
      setError(null);

      const provider = new OAuthProvider('microsoft.com');
      provider.setCustomParameters({
        prompt: 'select_account',
        tenant: 'common'
      });

      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const displayName = user.displayName || '';
      const nameParts = displayName.split(' ');

      setFormData(prev => ({
        ...prev,
        ssoProvider: 'microsoft',
        ssoUser: user,
        email: user.email || '',
        firstName: prev.firstName || nameParts[0] || '',
        lastName: prev.lastName || nameParts.slice(1).join(' ') || '',
        userId: user.uid
      }));

      setActiveStep(prev => prev + 1);
    } catch (err) {
      logger.error('Microsoft SSO error:', err);
      setError(err.message || 'Failed to sign in with Microsoft');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (!validateStep()) {
      setError('Please fill in all required fields');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      setActiveStep(prev => prev + 1);
    } catch (err) {
      logger.error('Error in wizard step:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const finalizeAccount = async () => {
    try {
      setLoading(true);
      setError(null);
      setFinalized(true);

      let userId;

      // Step 1: Create or use existing auth account
      if (formData.ssoProvider) {
        userId = formData.ssoUser.uid;
      } else {
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          formData.email,
          formData.password
        );

        if (!userCredential?.user) {
          throw new Error('Failed to create user account');
        }

        userId = userCredential.user.uid;

        await updateProfile(userCredential.user, {
          displayName: `${formData.firstName} ${formData.lastName}`
        });
      }

      // Step 2: Accept the system admin invitation (get role + permissions)
      const { systemRole, systemPermissions } = await systemAdminInviteService.acceptInvitation(
        inviteCode,
        userId
      );

      // Step 3: Create the user document as a system_admin account type
      const userRef = doc(db, 'users', userId);
      const systemAdminUserData = {
        uid: userId,
        email: formData.email,
        displayName: `${formData.firstName} ${formData.lastName}`,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone || null,

        // System admin specific fields
        accountType: 'system_admin',
        role: systemRole,
        systemPermissions: systemPermissions,
        isSystemAdmin: true,

        // No family association
        currentFamily: null,
        familyCode: null,

        // Metadata
        invitedBy: invitation.inviterId,
        inviteCode: inviteCode,
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true
      };

      await setDoc(userRef, systemAdminUserData);

      // Step 4: Navigate to system admin dashboard
      setTimeout(() => {
        navigate('/system-admin/dashboard');
      }, 2000);
    } catch (err) {
      logger.error('Error finalizing system admin account:', err);
      setError(err.message || 'Failed to complete setup. Please try again.');
      setFinalized(false);
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = () => {
    switch (activeStep) {
      case 0: // Verify Invitation
        return (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            {loadingInvitation ? (
              <>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Verifying invitation...</Typography>
              </>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <Alert severity="success">
                Invitation verified! Click Next to continue.
              </Alert>
            )}
          </Box>
        );

      case 1: // Create Account
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              {formData.ssoProvider ? 'Complete Your Profile' : 'Create Your System Admin Account'}
            </Typography>

            {invitation && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  You've been invited by <strong>{invitation.inviterName}</strong> to join as a{' '}
                  <Chip
                    label={getSystemRoleDisplayName(invitation.systemRole)}
                    size="small"
                    color="primary"
                    sx={{ mx: 0.5 }}
                  />
                </Typography>
                {invitation.customMessage && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Message: "{invitation.customMessage}"
                  </Typography>
                )}
              </Alert>
            )}

            {!formData.ssoProvider && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Sign up with:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<GoogleIcon />}
                      onClick={handleGoogleSSO}
                      disabled={loading}
                    >
                      Google
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleMicrosoftSSO}
                      disabled={loading}
                    >
                      Microsoft
                    </Button>
                  </Box>
                  <Divider sx={{ my: 2 }}>OR</Divider>
                </Box>

                <TextField
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  margin="normal"
                  helperText={invitation?.inviteeEmail ? 'Pre-filled from invitation' : ''}
                />
                <TextField
                  required
                  fullWidth
                  label="Password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  margin="normal"
                  helperText="Minimum 8 characters"
                />
              </>
            )}

            {formData.ssoProvider && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Signed in with {formData.ssoProvider === 'google' ? 'Google' : 'Microsoft'}
              </Alert>
            )}

            <TextField
              required
              fullWidth
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              required
              fullWidth
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Phone Number (Optional)"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              margin="normal"
            />
          </Box>
        );

      case 2: // Profile Picture
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Upload Profile Picture (Optional)
            </Typography>
            <Button
              variant="contained"
              component="label"
              fullWidth
              sx={{ mt: 2 }}
            >
              Choose File
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    setFormData(prev => ({
                      ...prev,
                      profilePicture: e.target.files[0]
                    }));
                  }
                }}
              />
            </Button>
            {formData.profilePicture && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: {formData.profilePicture.name}
              </Typography>
            )}
          </Box>
        );

      case 3: // Complete
        return (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Setting Up Your System Admin Account...
            </Typography>
            {loading && <CircularProgress sx={{ my: 2 }} />}
            {!loading && !error && (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Account created successfully!
                </Alert>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Role: <strong>{getSystemRoleDisplayName(invitation?.systemRole)}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Redirecting to System Admin Dashboard...
                </Typography>
              </>
            )}
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  if (loadingInvitation) {
    return (
      <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading invitation...</Typography>
      </Paper>
    );
  }

  // Show error for invalid/missing invitation before rendering wizard
  if (!invitation && !loadingInvitation) {
    return (
      <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AdminIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h5" component="h1">
            System Admin Onboarding
          </Typography>
        </Box>
        <Alert severity="error">
          {error || 'Invalid or missing invitation. Please contact the Root Administrator for a valid invitation link.'}
        </Alert>
        <Button
          variant="outlined"
          onClick={() => navigate('/login')}
          sx={{ mt: 2 }}
        >
          Go to Login
        </Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <AdminIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h5" component="h1">
          System Admin Onboarding
        </Typography>
      </Box>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        FamilyForLife IT Professional Account Setup
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mt: 2, mb: 4 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && activeStep > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {getStepContent()}

      {activeStep > 0 && activeStep < STEPS.length - 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          {activeStep > 1 && (
            <Button onClick={handleBack} disabled={loading}>
              Back
            </Button>
          )}
          <Box sx={{ flex: '1 1 auto' }} />
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading || !validateStep()}
          >
            {loading ? <CircularProgress size={24} /> : 'Next'}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default SystemAdminOnboardingWizard;
